/**
 * I5 / R19 — build-in-public strings (fr = source; en/ar typed against it).
 * Wired under `i5.bip`.
 */
import type { Catalog } from './fr';

const en: Catalog = {
  title: "Build in public",
  tabAssistant: "Step-by-step assistant",
  tabBip: "Build in public",
  subtitle:
    "Turn your GitHub repository’s real activity (merged PRs, commits, releases) into drafts for X, LinkedIn, Threads and Bluesky — in your brand voice.",
  viewerReadOnly: "Read-only: your role cannot generate or edit drafts.",
  github: {
    sectionTitle: "GitHub connection",
    readOnly: "Read-only",
    connectedAs: "Connected as {{login}}",
    modeOauth: "via OAuth",
    modePat: "via personal token",
    notConnected: "GitHub is not connected.",
    configRequired: "Configuration required",
    configRequiredHint:
      "The GitHub OAuth app is not configured on this environment yet. You can connect a read-only personal token below.",
    connectOauth: "Connect GitHub (OAuth)",
    redirecting: "Redirecting to GitHub…",
    patTitle: "Or use a read-only fine-grained personal access token",
    patHint:
      "Required permissions: Contents (read-only), Metadata (read-only), Pull requests (read-only). Any token carrying a write permission is refused.",
    patPlaceholder: "github_pat_…",
    patAck: "I confirm this token is read-only",
    patSubmit: "Connect token",
    patSaved: "GitHub token connected.",
    privateNote:
      "OAuth only requests read:user (public repositories): GitHub offers no read-only OAuth scope for private repositories. For a private repository, use a read-only fine-grained token.",
    disconnect: "Disconnect",
    disconnected: "GitHub disconnected.",
    refresh: "Refresh",
  },
  repo: {
    label: "Repository",
    placeholder: "Choose a repository…",
    loading: "Loading repositories…",
    saved: "Repository saved.",
    none: "No repository is accessible with this connection.",
    private: "private",
  },
  form: {
    sinceLabel: "Since",
    sinceHint: "Default: 7 days ago (90 days maximum).",
    changelogLabel: "Changelog URL (optional)",
    changelogHint:
      "A file of the connected repository on GitHub or a page of the repository’s website. robots.txt is honoured.",
    changelogPlaceholder: "https://github.com/org/repo/blob/main/CHANGELOG.md",
    networksLabel: "Networks",
    langLabel: "Language",
    generate: "Generate drafts",
    generating: "Generating…",
    noNetwork: "Choose at least one network.",
    needRepo: "Choose a repository first.",
  },
  langs: {
    fr: "French",
    en: "English",
    ar: "Arabic",
  },
  networks: {
    x_thread: "X thread",
    linkedin: "LinkedIn",
    threads: "Threads",
    bluesky: "Bluesky",
  },
  drafts: {
    title: "Drafts",
    empty:
      "No merged PR, commit or release in this period — nothing to post about.",
    sources: "Sources",
    sourceKinds: {
      pull_request: "PR",
      commit: "Commit",
      release: "Release",
      changelog: "Changelog",
    },
    counter: "{{n}} / {{limit}}",
    tweet: "Tweet {{n}}",
    edit: "Edit",
    saveEdit: "Save",
    cancelEdit: "Cancel",
    editSaved: "Draft updated.",
    copy: "Copy",
    copied: "Copied to clipboard.",
    saveDraft: "Save as draft",
    draftSaved: "Draft saved to your content.",
    openDraft: "Open draft",
    schedule: "Schedule",
    scheduleAt: "Publication date and time",
    scheduleConfirm: "Confirm schedule",
    scheduled: "Post scheduled.",
    scheduledOn: "Scheduled for {{date}}",
    overLimit: "Exceeds the network limit.",
    originLlm: "AI",
    originDeterministic: "Composed from your titles",
    llmFallback:
      "AI unavailable: drafts composed from the real titles of your activity.",
    truncated: "Shortened to fit the network limit.",
  },
  history: {
    title: "Generation history",
    empty: "No generation yet.",
    open: "Open",
    delete: "Delete",
    deleted: "Generation deleted.",
    deleteConfirm: "Delete this generation and its saved drafts?",
    draftsCount: "{{n}} drafts",
    current: "Shown",
  },
  errors: {
    generic: "Something went wrong. Please retry.",
    notConnected: "Connect GitHub to generate drafts.",
    noRepo: "Choose a repository before generating.",
    rateLimited: "GitHub API rate limit reached — try again later.",
  },
};

export default en;
